// src/components/AGTFit/workout/EditExerciseDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Slider,
  Grid,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { X, Plus, Minus, Dumbbell, Trash2 } from 'lucide-react';
import agtfitService from '../../service/agtfitService';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilo para diálogos estilo Apple como en ExerciseExplorer
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    // Eliminar bordes redondeados en pantalla completa
    borderRadius: theme.breakpoints.down('sm') ? 0 : 20,
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    overflow: 'hidden',
    // Asegurar que ocupa todo el espacio en pantalla completa
    height: theme.breakpoints.down('sm') ? '100%' : 'auto'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
    boxShadow: 'none',
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(211, 47, 47, 0.8)' 
    : theme.palette.error.main,
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(211, 47, 47, 0.9)' 
      : theme.palette.error.dark,
    boxShadow: 'none',
  },
}));

const CounterButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  borderRadius: '50%',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(235, 235, 240, 0.8)'),
  }
}));

// Contenedor de media mejorado para ajustarse perfectamente
const MediaContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 220,
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(20, 20, 22, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

// Campo de texto estilizado como en ExerciseExplorer
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  }
}));

// Contenedor para los datos del ejercicio
const ExerciseInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  marginBottom: theme.spacing(2)
}));

// Caja de estadísticas
const StatsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 107, 0, 0.2)'
    : (theme.palette.mode === 'dark' ? 'rgba(255, 107, 0, 0.15)' : 'rgba(255, 107, 0, 0.1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const EditExerciseDialog = ({ open, onClose, exercise }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [weight, setWeight] = useState(0);
  const [duration, setDuration] = useState(0); // en minutos
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Inicializar estados con los valores del ejercicio
  useEffect(() => {
    if (exercise) {
      setSets(exercise.sets || 3);
      setReps(exercise.reps || 10);
      setWeight(exercise.weight || 0);
      setDuration(exercise.duration || 0);
    }
  }, [exercise]);
  
  // Calcular calorías basadas en la duración y el ejercicio base
  const calculateCalories = () => {
    const baseCalories = exercise?.baseCalories || 5;
    const setCalories = baseCalories * sets;
    return Math.round(setCalories + (duration * 3)); // 3 calorías adicionales por minuto
  };
  
  // Manejar cambio en sets
  const handleSetsChange = (newValue) => {
    if (newValue >= 1 && newValue <= 10) setSets(newValue);
  };
  
  // Manejar cambio en reps
  const handleRepsChange = (newValue) => {
    if (newValue >= 1 && newValue <= 100) setReps(newValue);
  };
  
  // Manejar cambio en peso
  const handleWeightChange = (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 0) setWeight(value);
    else setWeight(0);
  };
  
  // Manejar cambio en duración
  const handleDurationChange = (event, newValue) => {
    setDuration(newValue);
  };
  
  // Actualizar el ejercicio
  const handleUpdateExercise = async () => {
    if (!exercise) return;
    
    setSubmitting(true);
    
    try {
      const exerciseData = {
        ...exercise,
        sets,
        reps,
        weight,
        duration,
        caloriesBurned: calculateCalories()
      };
      
      // Como no tenemos un endpoint específico para actualizar ejercicios,
      // eliminamos el anterior y creamos uno nuevo
      await agtfitService.deleteExercise(exercise.exerciseId);
      const response = await agtfitService.logExercise(exerciseData);
      
      if (response.success) {
        onClose(true, exerciseData, 'update');
      } else {
        console.error('Error updating exercise:', response.error);
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Eliminar el ejercicio
  const handleDeleteExercise = async () => {
    if (!exercise || !exercise.exerciseId) return;
    
    setDeleting(true);
    
    try {
      const response = await agtfitService.deleteExercise(exercise.exerciseId);
      
      if (response.success) {
        onClose(true, exercise, 'delete');
      } else {
        console.error('Error deleting exercise:', response.error);
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
    } finally {
      setDeleting(false);
    }
  };
  
  // Renderizar media (imagen o video) con reproducción continua
  const renderMedia = () => {
    if (!exercise) return null;
    
    if (exercise.videoUrl) {
      return (
        <MediaContainer>
          <video 
            width="100%" 
            height="100%" 
            autoPlay
            muted
            loop
            playsInline
            style={{ objectFit: 'cover' }}
          >
            <source src={exercise.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </MediaContainer>
      );
    } else if (exercise.imageUrl) {
      return (
        <MediaContainer>
          <img 
            src={exercise.imageUrl} 
            alt={exercise.name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover' 
            }}
          />
        </MediaContainer>
      );
    } else {
      return (
        <MediaContainer>
          <Dumbbell size={60} color={FIT_ORANGE} opacity={0.3} />
        </MediaContainer>
      );
    }
  };
  
  if (!exercise) return null;
  
  return (
    <StyledDialog
      open={open}
      onClose={() => onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
      }}>
        <Typography variant="h5" fontWeight={600}>
          Edit Exercise
        </Typography>
        
        <IconButton 
          onClick={() => onClose(false)} 
          sx={{ 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
            }
          }}
        >
          <X size={20} />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Mostrar imagen o video */}
        {renderMedia()}
        
        <ExerciseInfoBox>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {exercise.name}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {exercise.type || exercise.exerciseType || 'General'}
            {exercise.equipment && ` • ${exercise.equipment}`}
            {!exercise.equipment && ' • No Equipment'}
          </Typography>
          
          {exercise.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {exercise.description}
            </Typography>
          )}
        </ExerciseInfoBox>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sets
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CounterButton onClick={() => handleSetsChange(sets - 1)} disabled={sets <= 1}>
                <Minus size={16} />
              </CounterButton>
              <Typography variant="h6" sx={{ mx: 2, width: 20, textAlign: 'center' }}>
                {sets}
              </Typography>
              <CounterButton onClick={() => handleSetsChange(sets + 1)} disabled={sets >= 10}>
                <Plus size={16} />
              </CounterButton>
            </Box>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Reps
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CounterButton onClick={() => handleRepsChange(reps - 1)} disabled={reps <= 1}>
                <Minus size={16} />
              </CounterButton>
              <Typography variant="h6" sx={{ mx: 2, width: 20, textAlign: 'center' }}>
                {reps}
              </Typography>
              <CounterButton onClick={() => handleRepsChange(reps + 1)} disabled={reps >= 100}>
                <Plus size={16} />
              </CounterButton>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Weight (kg)
            </Typography>
            <StyledTextField
              type="number"
              value={weight}
              onChange={handleWeightChange}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Duration (minutes)
            </Typography>
            <Slider
              value={duration}
              onChange={handleDurationChange}
              min={0}
              max={60}
              step={1}
              valueLabelDisplay="auto"
              sx={{
                color: FIT_ORANGE,
                '& .MuiSlider-thumb': {
                  height: 24,
                  width: 24,
                  backgroundColor: '#fff',
                  border: `2px solid ${FIT_ORANGE}`,
                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(255, 107, 0, 0.16)',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <StatsBox>
              <Typography variant="body1" fontWeight={500}>
                Estimated calories:
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ 
                backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {calculateCalories()} kcal
              </Typography>
            </StatsBox>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 2 }}>
          <DeleteButton 
            startIcon={<Trash2 size={16} />}
            onClick={handleDeleteExercise}
            disabled={deleting || submitting}
            sx={{ flex: 1 }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </DeleteButton>
          
          <ActionButton 
            onClick={handleUpdateExercise}
            disabled={submitting || deleting}
            sx={{ flex: 1 }}
          >
            {submitting ? 'Updating...' : 'Update'}
          </ActionButton>
        </Box>
        
        <Button 
          onClick={() => onClose(false)} 
          fullWidth
          sx={{ 
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '15px',
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(235, 235, 240, 0.8)',
            }
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditExerciseDialog;